import { calcTotal } from "@majourneys/booking/pickers";
import type { PackageInfo } from "@majourneys/booking/pickers";
import { paysByLink } from "@majourneys/shared";
import type { DiscoverItem, Trip, TripDay } from "../types";
import { defaultStopTime } from "../components/trip-planner/trip-date";
import { partySize, splitParty, splitSize, type PartySplit, type TripParty } from "./trip-party";

/**
 * Turning a planned day into bookings.
 *
 * A stop in a trip is a catalogue id and nothing else, but a bucket line is a
 * booking: a package, a date, a time and a party, with a total that moves with
 * all of them. Everything here is the bridge between the two, kept pure so the
 * number a day shows and the number the bucket stores come from one function.
 *
 * The rules are the business model's, not this file's:
 * - **Activities** (spas, nightclubs, shows included) are paid on arrival. They
 *   are the only stops that go into the bucket from a trip.
 * - **Restaurants** are a free table, never a price, and the bucket cannot hold
 *   one (a table flow sends no package). They are left out and named.
 * - **Stays** (camps included) are paid online, so a "pay on arrival" button
 *   must never carry them. Left out and named, booked from their own page.
 * - **Explore** places are not bookable at all and say nothing.
 */

/** What a stop is, for the purpose of booking it. */
export type StopBookingKind = "activity" | "table" | "stay" | "explore";

export function stopBookingKind(
  item: Pick<DiscoverItem, "mode" | "segment" | "categorySlug" | "sourceSlug"> | null,
): StopBookingKind {
  if (!item || item.mode !== "bookable") return "explore";
  if (item.segment === "restaurants" || item.categorySlug === "restaurants") return "table";
  if (item.segment === "stays" || item.categorySlug === "camp" || paysByLink(item.sourceSlug)) {
    return "stay";
  }
  return "activity";
}

/**
 * The bookable side of one activity, as the host loads it from the API: just
 * what pricing and a bucket line need. The packages come back ordered with
 * the lead first, the same order the activity page quotes "from".
 */
export interface TripStopOffer {
  readonly activityId: string;
  readonly slug: string;
  readonly title: string;
  readonly cityName: string;
  readonly thumbnail: string | null;
  readonly template: string | null;
  readonly packages: readonly PackageInfo[];
  /** The venue's age bands; absent or null means the site defaults. */
  readonly infantMaxAge?: number | null;
  readonly childrenMaxAge?: number | null;
}

/**
 * A bucket line, field for field the web bucket's `BucketAddInput`. Declared
 * here because this package cannot import the app's bucket; the host maps it
 * across one to one.
 */
export interface TripBasketLine {
  readonly activityId: string;
  readonly activitySlug: string;
  readonly packageId: string;
  readonly date: string;
  readonly time: string | null;
  readonly adults: number;
  readonly children: number;
  readonly infants: number;
  readonly hours: number | null;
  readonly groups: number | null;
  readonly transport: boolean;
  readonly transportOutside: boolean;
  readonly thumbnail: string | null;
  readonly title: string;
  readonly cityName: string;
  readonly typePrice: 0 | 1 | 2 | 3;
  readonly maxGuests: number | null;
  readonly includedGuests: number | null;
  readonly pricing: PackageInfo["pricing"];
  /** Dirhams, whole, computed by the same `calcTotal` the booking sheet uses. */
  readonly priceSnapshot: number;
}

export interface StopQuote {
  readonly pkg: PackageInfo;
  readonly typePrice: 0 | 1 | 2 | 3;
  readonly groups: number | null;
  readonly hours: number | null;
  readonly transport: boolean;
  /** Who this package books from the party, and who it cannot take. */
  readonly split: PartySplit;
  /** Dirhams for everyone it books. */
  readonly total: number;
}

/**
 * Price one activity for the trip's party, the way the booking sheet would on
 * its defaults: the lead package, the party placed in the venue's age bands
 * (children it sells no ticket to are left out, owner rule), an hour on hourly
 * packages, enough vehicles to seat everyone on per-vehicle ones, and pickup
 * only when it is free (a paid pickup is the guest's choice, not ours).
 *
 * Null for anything a trip cannot book this way: no packages, or a nightly
 * package, which is a stay.
 */
export function quoteStop(offer: TripStopOffer, party: TripParty): StopQuote | null {
  const pkg = offer.packages[0];
  if (!pkg) return null;
  const typePrice = (pkg.typePrice ?? 0) as 0 | 1 | 2 | 3;
  if (typePrice === 2 || offer.template === "stay" || offer.template === "restaurant") return null;
  const split = splitParty(party, offer, pkg.pricing);
  const heads = Math.max(1, splitSize(split));
  const seats = pkg.maxGuests > 0 ? pkg.maxGuests : heads;
  const groups = typePrice === 3 ? Math.max(1, Math.ceil(heads / seats)) : null;
  const hours = typePrice === 1 ? 1 : null;
  const transport = pkg.pricing.transport === 0;
  const total = Math.round(
    calcTotal(
      typePrice,
      pkg,
      { adults: split.adults, children: split.children, infants: split.infants },
      hours ?? 1,
      transport,
      1,
      groups ?? 1,
      false,
    ),
  );
  return { pkg, typePrice, groups, hours, transport, split, total };
}

/** The bucket line for a quoted stop on a given day. */
export function basketLineFor(
  offer: TripStopOffer,
  quote: StopQuote,
  date: string,
  time: string | null,
): TripBasketLine {
  const { pkg, split } = quote;
  return {
    activityId: offer.activityId,
    activitySlug: offer.slug,
    packageId: pkg.id,
    date,
    // The time the day shows for the stop. The bucket line can be retimed,
    // and the team confirms it with the provider.
    time,
    adults: Math.max(1, split.adults),
    children: split.children,
    infants: split.infants,
    hours: quote.hours,
    groups: quote.groups,
    transport: quote.transport,
    transportOutside: false,
    thumbnail: offer.thumbnail,
    title: offer.title,
    cityName: offer.cityName,
    typePrice: quote.typePrice,
    maxGuests: pkg.maxGuests || null,
    includedGuests: pkg.includedGuests ?? null,
    pricing: pkg.pricing,
    priceSnapshot: quote.total,
  };
}

export type OfferLookup = (slug: string) => TripStopOffer | null | undefined;

/**
 * Everything a day's booking block needs to say and do.
 *
 * `offerFor` answers `undefined` while an offer is still loading and `null`
 * when it could not be loaded. Until every activity is priced from its
 * packages the total is the catalogue's lead price times the party, marked as
 * a floor (`exact: false`), so a guest is never shown a precise-looking number
 * the bucket will not repeat.
 */
export interface DayBookingPlan {
  /** Activities that will go into the bucket, in day order. */
  readonly activities: readonly DiscoverItem[];
  /**
   * Bucket lines for the activities whose offer has loaded and can be booked.
   * Equal in length to `activities` exactly when `exact` is true.
   */
  readonly lines: readonly TripBasketLine[];
  /** Dirhams due on the day for the party. 0 when nothing is priced. */
  readonly total: number;
  /** True when `total` is what the bucket will hold, line for line. */
  readonly exact: boolean;
  /** True while an offer the day needs is still loading. */
  readonly loading: boolean;
  /** Restaurants on the day: free tables, booked from their own page. */
  readonly tables: readonly DiscoverItem[];
  /** Stays on the day: paid online, booked from their own page. */
  readonly stays: readonly DiscoverItem[];
  /**
   * Activities that cannot take some of the party's children: priced and
   * booked for the others (owner rule), named so the day can say so.
   */
  readonly adultsOnly: readonly { readonly item: DiscoverItem; readonly minAge: number | null; readonly leftOut: number }[];
}

export function dayBookingPlan(
  day: TripDay,
  resolveItem: (itemId: string) => DiscoverItem | null,
  party: TripParty,
  date: string,
  offerFor?: OfferLookup,
): DayBookingPlan {
  const activities: DiscoverItem[] = [];
  const tables: DiscoverItem[] = [];
  const stays: DiscoverItem[] = [];
  const lines: TripBasketLine[] = [];
  const adultsOnly: { item: DiscoverItem; minAge: number | null; leftOut: number }[] = [];
  const everyone = partySize(party);
  let estimate = 0;
  let exactTotal = 0;
  let allExact = Boolean(offerFor);
  let loading = false;

  day.stops.forEach((stop, stopIndex) => {
    const item = resolveItem(stop.itemId);
    const kind = stopBookingKind(item);
    if (!item || kind === "explore") return;
    if (kind === "table") {
      if (!tables.some((t) => t.id === item.id)) tables.push(item);
      return;
    }
    if (kind === "stay") {
      if (!stays.some((s) => s.id === item.id)) stays.push(item);
      return;
    }
    activities.push(item);
    estimate += (item.priceMad ?? 0) > 0 ? Math.round(item.priceMad!) * everyone : 0;
    if (!offerFor) return;
    const offer = offerFor(item.sourceSlug);
    if (offer === undefined) {
      loading = true;
      allExact = false;
      return;
    }
    const quote = offer ? quoteStop(offer, party) : null;
    if (quote && quote.split.leftOut > 0 && !adultsOnly.some((a) => a.item.id === item.id)) {
      adultsOnly.push({ item, minAge: quote.split.minAge, leftOut: quote.split.leftOut });
    }
    if (!offer || !quote) {
      allExact = false;
      return;
    }
    exactTotal += quote.total;
    // The time the timeline shows: the guest's own, else the slot it was
    // given by its place in the day.
    const time = stop.startTime ?? defaultStopTime(stopIndex).startTime;
    lines.push(basketLineFor(offer, quote, date, time));
  });

  const exact = allExact && activities.length > 0 && lines.length === activities.length;
  return {
    activities,
    lines,
    total: exact ? exactTotal : estimate,
    exact,
    loading,
    tables,
    stays,
    adultsOnly,
  };
}

/** Whether a YYYY-MM-DD day is before `today` (also YYYY-MM-DD). */
export function isPastDay(date: string, today: string): boolean {
  return date < today;
}

/** The bookable activity slugs across a trip, once each, for preloading offers. */
export function tripActivitySlugs(
  trip: Trip,
  resolveItem: (itemId: string) => DiscoverItem | null,
): string[] {
  const out = new Set<string>();
  for (const day of trip.days) {
    for (const stop of day.stops) {
      const item = resolveItem(stop.itemId);
      if (item && stopBookingKind(item) === "activity") out.add(item.sourceSlug);
    }
  }
  return [...out];
}

/**
 * A camp or villa's price for its nights, from its own packages and the
 * party, exactly as the server records a booking (`reservationBasePrice`):
 * a per-night package is its price times the nights; a per-person package is
 * charged once, so it is only quoted for a single night, where that is the
 * true price. Anything else is null and the card sends the guest to the page.
 */
export function quoteVenueStay(offer: TripStopOffer, party: TripParty, nights: number): number | null {
  const pkg = offer.packages[0];
  if (!pkg || nights < 1) return null;
  const typePrice = (pkg.typePrice ?? 0) as 0 | 1 | 2 | 3;
  if (typePrice !== 2 && !(typePrice === 0 && nights === 1)) return null;
  const split = splitParty(party, offer, pkg.pricing);
  if (splitSize(split) < 1) return null;
  const perStay = calcTotal(
    typePrice,
    pkg,
    { adults: split.adults, children: split.children, infants: split.infants },
    1,
    false,
    nights,
  );
  return perStay > 0 ? Math.round(perStay) : null;
}

